import React, { useState, useEffect, useMemo } from 'react';
import './flightButton2.less';
import { Button, Modal, Tooltip } from 'antd';
import { useSelector } from 'react-redux';
import { RootState } from '../../store/store';
import useTranslationForPersonType from '../../hooks/useGetCorrectName';
import {
  FightTermsType,
  FlightsInfo,
  FlightType,
  PriceDetails,
} from '../../interfaces/flightInterface';
import useGetGrossPrice from '../../hooks/useGetGrossPrice';
import { IoInformationOutline } from 'react-icons/io5';
import { Link } from 'react-router-dom';
import AgentProductTerms from '../AgentCommission/AgentProductTerms';
import FlightTerms from '../AgentCommission/FlightTerms';

import { TenantTsType } from '@skygroup/shared/constants/tenantTs';
import { MainProductType } from '../../modules/ShoppingCart/interfaces';
import { useCalcTwoPrices } from '../../utils/convertEurToUsd';
import { RiExchangeDollarFill } from 'react-icons/ri';

interface Props {
  flightPrice: PriceDetails;
  handlePurchase?: (
    type: FlightType,
    f1: string,
    returnLink?: boolean
  ) => void | string;
  type?: FlightType;
  f1?: string;
  pax: {
    adult: number;
    child: number;
    infant: number;
  };
  selectedTrolly?: {
    [key: string]: boolean;
  };
  selectedSuitcase?: {
    [key: string]: boolean;
  };
  suitcasePrice?: number;
  trollyPrice?: number;
  viewPage?: boolean;
  identifier?: string;
  suggested_commission?: number;
  buttonText?: string;
  flightsTerms?: any;
  description?: string;
  isPreviewMode?: boolean;
  showProductPrice?: boolean;
  noAvailabilityForThisPrice: boolean;
  showRatesTooltip?: boolean;
  showPriceDifference?: boolean;
  hideFlightButton?: boolean;
  isDynamicPackage?: boolean;
  isCompact?: boolean;
  /**
   * Total-only mode (e.g. chat/bot cards) where the searched pax is unknown.
   * Hides the per-passenger price and the passenger-count breakdown, and shows
   * the total only when the API provides it (never the rate×pax fallback, which
   * would be wrong without the real pax).
   */
  hidePaxBreakdown?: boolean;
}

const FlightButton2 = ({
  flightPrice,
  handlePurchase,
  type,
  f1,
  pax,
  selectedTrolly,
  selectedSuitcase,
  suitcasePrice,
  trollyPrice,
  viewPage = false,
  identifier,
  suggested_commission,
  buttonText,
  flightsTerms,
  isPreviewMode,
  showProductPrice = true,
  noAvailabilityForThisPrice,
  showRatesTooltip,
  showPriceDifference,
  hideFlightButton = false,
  isDynamicPackage = false,
  isCompact = false,
  hidePaxBreakdown = false,
}: Props) => {
  const { rates } = useSelector(
    (state: RootState) => state.config.config.siteConfig
  );
  const ratesArr = useMemo(() => rates && Object.entries(rates), [rates]);

  const {
    flightButton: t,
    agentCommission: agentCommissionT,
    general: general_t,
    viewFlight: viewFlight_t,
  } = useSelector((data: RootState) => data.config.translations);
  const [modalOpenData, setModalOpenData] = useState<any>(null);
  const [showTermsWithApiIdentifiers, setShowTermsWithApiIdentifiers] =
    useState([]);

  const totalTrollyPrice = Object.keys(selectedTrolly || {}).reduce(
    (acc, curr) => {
      if (selectedTrolly && selectedTrolly[curr]) {
        return acc + (trollyPrice || 0);
      }
      return acc;
    },
    0
  );
  const totalSuitcasePrice = Object.keys(selectedSuitcase || {}).reduce(
    (acc, curr) => {
      if (selectedSuitcase && selectedSuitcase[curr]) {
        return acc + (suitcasePrice || 0);
      }
      return acc;
    },
    0
  );

  const totalPrice =
    (flightPrice.adult ?? 0) * (pax.adult ?? 0) +
    (flightPrice.child ?? 0) * (pax.child ?? 0) +
    (flightPrice.infant ?? 0) * (pax.infant ?? 0) +
    totalSuitcasePrice +
    totalTrollyPrice;

  // Use existing total or calculated total - don't mutate the original object
  const apiTotal = flightPrice.total ?? flightPrice.grandTotal;
  const finalTotalPrice = apiTotal ?? totalPrice;
  // In total-only mode (unknown pax) never fall back to rate×pax; show the
  // total only when the API actually provided it.
  const displayTotal = hidePaxBreakdown ? apiTotal : finalTotalPrice;

  const totalPaxNoInfants =
    (pax.adult ? Number(pax.adult) : 0) + (pax.child ? Number(pax.child) : 0);

  const getPersonTypeTranslation = useTranslationForPersonType();

  const { ShoppingCartV2 } = useSelector(
    (state: RootState) => state.shoppingCartV2
  );

  const isButtonDisalbed =
    hideFlightButton ||
    ShoppingCartV2?.products?.find((product) => {
      if (
        product.type === MainProductType.oneWayFlights &&
        product?.subProducts?.length === 2
      ) {
        const twoOneWayIdentifier = `${product?.subProducts[0].identifier}&${product?.subProducts[1].identifier}`;
        return twoOneWayIdentifier === identifier;
      }
      return product.identifier === identifier;
    });

  const getGrossPrice = useGetGrossPrice();

  return (
    <div
      className={`flight-button-2${isDynamicPackage ? ' dynamic-package' : ''}${
        isCompact ? ' compact' : ''
      }`}
    >
      {!hideFlightButton &&
        flightsTerms &&
        Object.keys(flightsTerms).length > 0 &&
        Object.entries(flightsTerms).map(
          ([key, val]: [key: string, val: any]) => {
            if (
              !val?.[0]?.identifier ||
              (val?.[0]?.identifier && val?.[0]?.ts !== TenantTsType.ALP)
            ) {
              return null;
            }

            return (
              <div
                className="info"
                key={key}
                onClick={() =>
                  setModalOpenData(() => {
                    return val?.[0]?.identifier ? val?.[0] : val;
                  })
                }
              >
                <span>
                  <IoInformationOutline size={40} />
                </span>
                <span>
                  {t.cardConditions}{' '}
                  {Object.keys(flightsTerms).length > 1 ? key : ''}
                </span>
              </div>
            );
          }
        )}

      {!isPreviewMode && (
        <>
          <Tooltip
            className="price-wrapper"
            title={
              !showRatesTooltip || !ratesArr?.length ? undefined : (
                <>
                  {ratesArr.map((curr: any, index) => (
                    <span
                      key={`${index}_${curr?.[0]}`}
                      className={'exchange-rate'}
                      style={{
                        margin: '5px 0',
                        backgroundColor: 'transparent',
                      }}
                    >
                      {curr?.[0] + ' ' + curr?.[1].toFixed(4)}
                    </span>
                  ))}
                </>
              )
            }
          >
            <>
              {!hidePaxBreakdown && (
                <div className="average-price-wrapper">
                  <div className="single-price-text">
                    {t.pricePerPassenger}
                  </div>
                  <div className="single-price">
                    {showPriceDifference && finalTotalPrice > 0 && '+ '}
                    {showPriceDifference && finalTotalPrice == 0
                      ? general_t.sameRates
                      : `${getGrossPrice(
                          finalTotalPrice / totalPaxNoInfants,
                          suggested_commission
                        )} ${String.fromCharCode(160)} ${
                          flightPrice.currency_sign
                        }`}
                  </div>
                </div>
              )}
              <div className="total-price-wrapper">
                {!hidePaxBreakdown && !showPriceDifference && (
                  <div className="number-of-passengers">
                    {pax.adult > 0 && (
                      <div>
                        {pax.adult}{' '}
                        {getPersonTypeTranslation('adult', Number(pax.adult))}
                      </div>
                    )}
                    {Number(pax.child) > 0 && (
                      <div>
                        {pax.child}{' '}
                        {getPersonTypeTranslation('child', Number(pax.child))}
                      </div>
                    )}
                    {Number(pax.infant) > 0 && (
                      <div>
                        {pax.infant}{' '}
                        {getPersonTypeTranslation('infant', Number(pax.infant))}
                      </div>
                    )}
                  </div>
                )}
                {!hidePaxBreakdown && showPriceDifference && (
                  <div className="number-of-passengers">
                    <div>{viewFlight_t.totalChange}</div>
                  </div>
                )}
                {hidePaxBreakdown && displayTotal != null && (
                  <div className="number-of-passengers">
                    <div>{t.total}</div>
                  </div>
                )}
                {displayTotal != null && (
                  <div className="total">
                    {showPriceDifference && finalTotalPrice > 0 && '+ '}
                    {getGrossPrice(displayTotal, suggested_commission)}{' '}
                    {String.fromCharCode(160)}
                    {flightPrice?.currency_sign}
                  </div>
                )}
              </div>
            </>
          </Tooltip>
          {noAvailabilityForThisPrice && (
            <div className="no-availability-text">
              {t.no_availability_for_this_flight_price}
            </div>
          )}
          {!noAvailabilityForThisPrice && !hideFlightButton && (
            <div>
              {
                viewPage ? (
                  <Button
                    className="btn-style-5"
                    size="large"
                    onClick={() => {
                      (handlePurchase as any)();
                    }}
                    disabled={!!isButtonDisalbed}
                  >
                    {t.addToCart}
                  </Button>
                ) : (
                  <Button
                    className="btn-style-5"
                    size="large"
                    disabled={!!isButtonDisalbed}
                    onClick={() => {
                      type && f1 && handlePurchase && handlePurchase(type, f1);
                    }}
                  >
                    {buttonText ?? t.continueToOrder}
                  </Button>
                )
                // TODO: fix scenario when we render one ways - that do the select flight
                //     (
                //   <Link
                //     to={
                //       type && f1 && handlePurchase
                //         ? (handlePurchase(type, f1, true) as string)
                //         : ''
                //     }
                //     target={'_blank'}
                //   >
                //     <Button
                //       className="btn-style-5"
                //       size="large"
                //       disabled={!!isButtonDisalbed}
                //       onClick={undefined}
                //     >
                //       {buttonText ?? t.continueToOrder}
                //     </Button>
                //   </Link>
                // )
              }
            </div>
          )}
        </>
      )}

      <Modal
        open={!!modalOpenData}
        onCancel={() => setModalOpenData(null)}
        footer={null}
        destroyOnClose
        className={`terms-info-modal`}
      >
        {modalOpenData && !modalOpenData?.identifier && identifier && (
          <FlightTerms identifier={identifier} flightsTerms={modalOpenData} />
        )}
        {modalOpenData &&
          modalOpenData?.ts === TenantTsType.ALP &&
          modalOpenData?.identifier && (
            <AgentProductTerms
              identifier={modalOpenData?.identifier}
              description={modalOpenData?.description}
            />
          )}
      </Modal>
    </div>
  );
};

export default FlightButton2;
